import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";

const navItems = [
  { href: "#flow", label: "Diagnóstico", num: "01" },
  { href: "#services", label: "Servicios", num: "02" },
  { href: "#portfolio", label: "Portafolio", num: "03" },
  { href: "#cases", label: "Casos", num: "04" },
];

const Header = () => {
  const [visible, setVisible] = useState(true);
  const [atTop, setAtTop] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const lastY = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setAtTop(y < 80);
      if (y < 80) {
        setVisible(true);
      } else if (y > lastY.current + 8) {
        setVisible(false);
        setMobileOpen(false);
      } else if (y < lastY.current - 4) {
        setVisible(true);
      }
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: visible ? 0 : -120 }}
        transition={{ duration: 0.45, ease: [0.23, 1, 0.32, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          atTop ? "" : "header-glass"
        }`}
        data-testid="main-header"
      >
        <nav className="max-w-[1440px] mx-auto px-4 lg:px-6 h-[64px] lg:h-[72px] flex items-center relative">
          {/* Left nav */}
          <div className="hidden lg:flex items-center gap-10">
            {navItems.slice(0, 2).map((item) => (
              <a
                key={item.num}
                href={item.href}
                className="nav-link"
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <span className="nav-num">{item.num}</span>
                {item.label}
              </a>
            ))}
          </div>

          {/* Center logo — absolute center */}
          <a
            href="/"
            className="absolute left-1/2 -translate-x-1/2 flex items-center"
            data-testid="logo-link"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_5035be6a-7db4-4e98-9a71-35ab08f9d865/artifacts/z23j60x7_1.svg"
              alt="Disruptiva"
              className="hidden lg:block h-auto w-52"
            />
            {/* Mobile: same logo, smaller */}
            <img
              src="https://customer-assets.emergentagent.com/job_5035be6a-7db4-4e98-9a71-35ab08f9d865/artifacts/z23j60x7_1.svg"
              alt="Disruptiva"
              className="lg:hidden h-auto w-36"
            />
          </a>

          {/* Right nav + CTA */}
          <div className="hidden lg:flex items-center gap-10 ml-auto">
            {navItems.slice(2).map((item) => (
              <a
                key={item.num}
                href={item.href}
                className="nav-link"
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <span className="nav-num">{item.num}</span>
                {item.label}
              </a>
            ))}
            <Link to="/blog" className="nav-link" data-testid="nav-blog">
              <span className="nav-num">05</span>
              Blog
            </Link>
            <a href="#contact" className="nav-cta" data-testid="header-cta">
              Hablemos
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden ml-auto p-2.5 rounded-xl text-white/50 hover:text-white hover:bg-white/[0.04] transition-all z-10"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </nav>
        <div className="header-line" />
      </motion.header>

      {/* Mobile overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
            className="fixed inset-0 z-40 bg-black/98 backdrop-blur-3xl pt-20 px-6"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-0 mt-4">
              {navItems.map((item, i) => (
                <motion.a
                  key={item.num}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.06 }}
                  className="mobile-nav-item"
                >
                  <span className="mobile-nav-num">{item.num}</span>
                  <span className="font-space text-2xl font-semibold text-white/80">{item.label}</span>
                </motion.a>
              ))}
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.24 }}>
                <Link
                  to="/blog"
                  onClick={() => setMobileOpen(false)}
                  className="mobile-nav-item"
                >
                  <span className="mobile-nav-num">05</span>
                  <span className="font-space text-2xl font-semibold text-white/80">Blog</span>
                </Link>
              </motion.div>
              <motion.a
                href="#contact"
                onClick={() => setMobileOpen(false)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="btn-glass mt-10 justify-center py-5"
              >
                Hablemos
                <ArrowRight className="w-4 h-4" />
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
